import { numberIsUndefinedOrNull } from "./numberIsUndefinedOrNull";

export enum Metric {
  "m³" = "m³",
  "ft³" = "ft³",
  "m3" = "m3",
  "ft3" = "ft3",
  "lb" = "lb",
  "lbs" = "lbs",
  "kg" = "kg",
  "mm" = "mm",
  "m" = "m",
  "cm" = "cm",
}

export enum MappingNameMetric {
  "m³" = "m³",
  "ft³" = "ft³",
  "m3" = "m3",
  "ft3" = "ft3",
  "lb" = "lb",
  "lbs" = "lbs",
  "kg" = "Kg",
  "mm" = "mm",
  "m" = "m",
  "cm" = "cm",
}

export const DefaultMetrics = {
  VOLUME: "M3",
  WEIGHT: "KG",
};

const MetricDecimalPlace = {
  [Metric.m3]: 2,
  [Metric.ft3]: 0,
};

export const DEFAULT_MAX_LENGTH = 2;
export const DEFAULT_DECIMALS = 2;

// 1 m3 means 35.3146667 ft3
export const CUBIC_METER_TO_CUBIC_FOOT_RATIO = 35.3146667;

// 1 ft3 means 0.283168466 m3
export const CUBIC_FOOT_TO_CUBIC_METER_RATIO = 0.0283168466;

// 1 kg means 2.20462 lb
export const KILOGRAM_TO_POUND_RATIO = 2.20462;

// 1 lb means 0.453592 kg
export const POUND_TO_KILOGRAM_RATIO = 0.453592;

export const isM3 = (metric: Metric) => metric === Metric["m³"] || Metric.m3;
export const ifFt3 = (metric: Metric) => metric === Metric["ft³"] || Metric.ft3;

export const isFtMetric = (measurement?: string) =>
  !!measurement && measurement.toString().toLowerCase().includes("ft");

export const isM3Metric = (measurement?: string) => {
  const value = measurement?.toString().toLowerCase();
  return (
    !!value &&
    (value.includes("m3") || value.includes("m³") || value === "m")
  );
};

export const convertMetricVolume = (
  volume?: string | number,
  metric?: "ft3" | "m3" | string,
  quantity?: string | number,
  returnType?: "string" | "number"
) => {
  const volumeVariable = volume ?? null;

  if (volumeVariable === null) return 0;
  if (Number.isNaN(Number(volumeVariable))) return volumeVariable;
  const volumeValue = Number(volumeVariable);

  let calculate = 0;
  let calculateConverted = calculate.toString();

  if (isFtMetric(metric)) {
    calculate = Number(volumeValue * CUBIC_METER_TO_CUBIC_FOOT_RATIO * (Number(quantity) || 1));
    calculateConverted = calculate.toFixed(MetricDecimalPlace[Metric.ft3]);
  } else {
    calculate = volumeValue * (Number(quantity) || 1);
    calculateConverted = calculate.toFixed(MetricDecimalPlace[Metric.m3]);
  }

  if (returnType === "number") {
    return Number(calculateConverted);
  }

  return calculateConverted;
};

// This method displays the value for the user based on the measurement
export const handleGetConvertValue = (
  value?: number,
  measurement?: string | undefined,
  decimalPlaces?: number
): string | number => {
  if (!measurement) return Number(value);

  if (value === undefined) return 0;

  if (isFtMetric(measurement)) {
    const valueFoot = Number(value * CUBIC_METER_TO_CUBIC_FOOT_RATIO);

    if (!numberIsUndefinedOrNull(decimalPlaces)) return Number(valueFoot)?.toFixed(decimalPlaces);

    return Math.round(valueFoot);
  }

  if (measurement.toLowerCase().includes("lb")) {
    const valuePounds = Number(value * KILOGRAM_TO_POUND_RATIO);

    if (!numberIsUndefinedOrNull(decimalPlaces)) return Number(valuePounds)?.toFixed(decimalPlaces);

    return Math.round(valuePounds);
  }

  if (measurement.includes("m³")) {
    if (!numberIsUndefinedOrNull(decimalPlaces)) return Number(value)?.toFixed(decimalPlaces);
  }

  return Number(value)?.toFixed(decimalPlaces ?? DEFAULT_MAX_LENGTH);
};

// This method saves the value based on user input measurement
export const handleSaveConvertValue = (value: string | number, measurement: string = "m3") => {
  if (isFtMetric(measurement)) return Number(value) * CUBIC_FOOT_TO_CUBIC_METER_RATIO;
  if (measurement.toLowerCase().includes("lb")) return Number(value) / KILOGRAM_TO_POUND_RATIO;

  return Number(value);
};

export const convertBasicUnits = () => {
  const convertMillimetersToMeters = (value?: number) => {
    if (!value || value <= 0) return 0;

    return parseFloat((value / 1000).toFixed(7));
  };

  const convertMetersToMillimeters = (value?: number) => {
    if (!value || value <= 0) return 0;

    return parseFloat((value * 1000).toFixed(4));
  };

  return {
    convertMillimetersToMeters,
    convertMetersToMillimeters,
  };
};

// Convert nautical miles value to kilometers
export const convertNauticalMilesToKilometers = (value?: number) => {
  if (!value || value <= 0) return 0;

  return value * 1.852;
};

export const formatDecimalToWhole = (weight: number | string) => {
  // Check if the weight is very close to a whole number
  if (Math.abs(Number(weight) - Math.round(Number(weight))) < 0.05) {
    // Round to 2 decimal places for whole or near-whole numbers
    return Math.round(Number(weight)).toFixed(2);
  }
  // For other cases, keep the original number
  return weight;
};
